<script lang="ts">
  /**
   * The account deletion confirm modal (R5.5): names the account, one
   * irreversible-loss line, an export-all safeguard anchor, and a confirm
   * field — `[ Delete account ]` stays `aria-disabled` (never `disabled`: a
   * disabled control is skipped by screen readers entirely) until the trimmed
   * field equals the account's email exactly, case-sensitive.
   *
   * Mounting contract: `DeleteNotebookModal`'s — a blur-modal sibling with the
   * surface behind `inert`, the heading taking focus on mount, `Esc`
   * cancelling. Authors nothing itself: `onConfirm` hands the decision to the
   * container, which owns the DELETE and the sign-out that follows it.
   */
  import Icon from './Icon.svelte';
  import Check from 'lucide/dist/esm/icons/check.mjs';
  import Download from 'lucide/dist/esm/icons/download.mjs';
  import X from 'lucide/dist/esm/icons/x.mjs';
  import { ANONYMOUS_OWNER_ID, type NotebookSummary } from '../storage/operations';
  import type { Account } from '../storage/account';

  interface Props {
    account: Account;
    /** The export-all safeguard needs the notebook list to select. */
    notebooks: NotebookSummary[];
    onConfirm: () => void;
    onCancel: () => void;
    /** The owner this browser is acting as (R7) — the export anchor's path. */
    ownerId?: string;
  }

  let {
    account,
    notebooks,
    onConfirm,
    onCancel,
    ownerId = ANONYMOUS_OWNER_ID,
  }: Props = $props();

  /** R5.4's shape: one bundle, ids repeated in list order. The server rejects
   * an empty selection, so the anchor renders only over a non-empty list. */
  const exportAllHref = $derived(
    notebooks.length === 0
      ? null
      : `/api/owners/${ownerId}/export?${new URLSearchParams(
          notebooks.map((notebook) => ['notebook_id', notebook.notebook_id]),
        )}`,
  );

  let headingEl = $state<HTMLHeadingElement | null>(null);
  $effect(() => {
    headingEl?.focus();
  });

  let confirmText = $state('');

  /** Trimmed, then exact — case-sensitive: ` Ada@example.com ` is not `ada@example.com`. */
  const armed = $derived(confirmText.trim() === account.email);

  function commit() {
    if (!armed) return;
    onConfirm();
  }

  function onkeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onCancel();
    }
  }
</script>

<!--
  The blur backdrop and the sheet are one element: the blur is decoration,
  `inert` on the SURFACE BEHIND is what controls (the takeover's rule).
-->
<div
  class="modal-backdrop"
  data-testid="delete-account-modal"
  role="dialog"
  aria-modal="true"
  aria-labelledby="delete-account-heading"
  tabindex="-1"
  {onkeydown}
>
  <div class="modal-sheet">
    <h1 id="delete-account-heading" bind:this={headingEl} tabindex="-1" data-testid="delete-account-heading">
      Delete account?
    </h1>

    <p>
      This permanently deletes your account <strong>{account.email}</strong>
      and every notebook it owns, on every device.
      This cannot be undone.
    </p>

    <!-- The last-chance safeguard: export everything before the account is
         gone. The owner is the ACTING one (R7) — the gate refuses the
         anonymous path with a 401 for every signed-in user. -->
    {#if exportAllHref}
      <a
        class="action"
        data-testid="delete-account-export"
        href={exportAllHref}
        download="all-notebooks.af1-export.json"
      >
        <Icon node={Download} size={13} />
        <span>[ Export all notebooks first ]</span>
      </a>
    {/if}

    <label class="field">
      <span class="field-label">Type {account.email} to confirm</span>
      <input
        type="text"
        data-testid="delete-account-confirm-input"
        bind:value={confirmText}
        autocomplete="off"
        spellcheck="false"
      />
    </label>

    <div class="modal-actions">
      <button
        class="action"
        type="button"
        data-testid="delete-account-confirm"
        aria-disabled={armed ? 'false' : 'true'}
        onclick={commit}
      ><Icon node={Check} />[ Delete account ]</button>
      <button
        class="action"
        type="button"
        data-testid="delete-account-cancel"
        onclick={onCancel}
      ><Icon node={X} />[ Cancel ]</button>
    </div>
  </div>
</div>

<style>
  .modal-backdrop {
    position: fixed;
    inset: 0;
    display: grid;
    place-items: center;
    background: color-mix(in srgb, var(--ink-muted) 20%, transparent);
    backdrop-filter: blur(2px);
    z-index: 30;
  }

  .modal-sheet {
    width: min(100%, 32em);
    background: var(--paper);
    box-shadow: var(--shadow-modal);
    padding: 1.5rem 2rem;
  }

  h1 {
    font-size: 1.25rem;
    font-weight: normal;
    outline: none;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 2px;
    margin-top: 0.9rem;
  }

  .field-label {
    font-size: 12px;
    color: var(--ink-muted);
  }

  input {
    font: inherit;
    font-size: 16px;
    border: none;
    border-bottom: 1px solid color-mix(in srgb, var(--ink-muted) 70%, transparent);
    background: transparent;
    padding: 2px 0;
  }

  .modal-actions {
    display: flex;
    gap: 0.75rem;
    margin-top: 1.4rem;
  }
</style>
